
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface BookingDetails {
  sessionId: string;
  route: string;
  passengers: number;
  departureDate: string;
  returnDate?: string;
  travelType: string;
  airline: string;
  flightClass: string;
  customerName: string;
  customerEmail: string;
  totalAmount: number;
  currency: string;
  paymentStatus: string;
  bookingReference: string;
}

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('No booking session found');
      setLoading(false);
      return;
    }

    const processBooking = async () => {
      try {
        // Process the successful payment and create order
        const webhookResponse = await fetch(
          `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/stripe-webhook`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          }
        );

        if (!webhookResponse.ok) {
          throw new Error('Failed to process booking');
        }

        const webhookData = await webhookResponse.json();
        
        if (webhookData.success) {
          // Generate booking reference
          const bookingRef = `MV${Date.now().toString().slice(-8)}${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
          
          // Set mock booking details (in real implementation, this would come from the webhook response)
          setBookingDetails({
            sessionId,
            route: 'New York → London', // This would come from session metadata
            passengers: 1,
            departureDate: new Date().toISOString().split('T')[0],
            travelType: 'round-trip',
            airline: 'British Airways',
            flightClass: 'Economy Class',
            customerName: 'John Doe',
            customerEmail: 'john@example.com',
            totalAmount: 596,
            currency: 'USD',
            paymentStatus: 'completed',
            bookingReference: bookingRef
          });
        } else {
          throw new Error('Booking processing failed');
        }
      } catch (err) {
        console.error('Error processing booking:', err);
        setError('Failed to process your booking. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    processBooking();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <main className="pt-20">
          <div className="container mx-auto px-6 py-20">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-6">
                <i className="ri-loader-4-line text-2xl text-white animate-spin"></i>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Processing Your Booking...
              </h1>
              <p className="text-gray-300">
                Please wait while we confirm your flight booking.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <main className="pt-20">
          <div className="container mx-auto px-6 py-20">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-red-600 rounded-full mx-auto mb-6">
                <i className="ri-error-warning-line text-2xl text-white"></i>
              </div>
              <h1 className="text-3xl font-bold text-white mb-4">
                Booking Error
              </h1>
              <p className="text-gray-300 mb-8">{error}</p>
              <div className="space-y-4">
                <Link
                  to="/pricing"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                >
                  Try Booking Again
                </Link>
                <div className="text-gray-400">
                  <p>Need help? Contact our support team:</p>
                  <p className="font-medium text-white">support@maveskytravels.com</p>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />

      <main className="pt-20">
        {/* Success Hero */}
        <section className="py-20 bg-gradient-to-r from-green-900 to-blue-900">
          <div className="container mx-auto px-6 text-center">
            <div className="w-20 h-20 flex items-center justify-center bg-green-600 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-3xl text-white"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Booking Confirmed!
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Your flight has been successfully booked. We've sent a confirmation email with all the details.
            </p>
          </div>
        </section>

        {/* Booking Details */}
        {bookingDetails && (
          <section className="py-20">
            <div className="container mx-auto px-6">
              <div className="max-w-4xl mx-auto">
                {/* Booking Reference */}
                <div className="bg-green-900/30 border border-green-800 rounded-xl p-8 mb-8 text-center">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    Booking Reference
                  </h2>
                  <div className="text-4xl font-mono font-bold text-green-400 mb-4">
                    {bookingDetails.bookingReference}
                  </div>
                  <p className="text-gray-300">
                    Please save this reference number for your records
                  </p>
                </div>

                {/* Flight Details */}
                <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                      <i className="ri-plane-line text-white"></i>
                    </div>
                    Flight Details
                  </h3>

                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Route:</span>
                        <span className="text-white font-semibold">{bookingDetails.route}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Passengers:</span>
                        <span className="text-white">{bookingDetails.passengers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Class:</span>
                        <span className="text-white">{bookingDetails.flightClass}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Airline:</span>
                        <span className="text-white">{bookingDetails.airline}</span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Travel Type:</span>
                        <span className="text-white capitalize">{bookingDetails.travelType.replace('-', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Departure:</span>
                        <span className="text-white">{new Date(bookingDetails.departureDate).toLocaleDateString()}</span>
                      </div>
                      {bookingDetails.returnDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Return:</span>
                          <span className="text-white">{new Date(bookingDetails.returnDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-400">Status:</span>
                        <span className="text-green-400 font-semibold capitalize">{bookingDetails.paymentStatus}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-600 mt-6 pt-6">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-semibold text-white">Total Paid:</span>
                      <span className="text-3xl font-bold text-green-400">
                        ${bookingDetails.totalAmount.toLocaleString()} {bookingDetails.currency}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-purple-600 rounded-lg mr-3">
                      <i className="ri-user-line text-white"></i>
                    </div>
                    Passenger Information
                  </h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-400 mb-2">Full Name</label>
                      <div className="text-white font-semibold">{bookingDetails.customerName}</div>
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2">Email Address</label>
                      <div className="text-white font-semibold">{bookingDetails.customerEmail}</div>
                    </div>
                  </div>
                </div>

                {/* Next Steps */}
                <div className="bg-blue-900/30 border border-blue-800 rounded-xl p-8 mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <div className="w-8 h-8 flex items-center justify-center bg-blue-600 rounded-lg mr-3">
                      <i className="ri-information-line text-white"></i>
                    </div>
                    What's Next?
                  </h3>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <h4 className="text-white font-semibold mb-2">Check Your Email</h4>
                      <p className="text-gray-300 text-sm">
                        We've sent detailed booking confirmation and e-tickets to your email
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                        <i className="ri-calendar-check-line text-white text-xl"></i>
                      </div>
                      <h4 className="text-white font-semibold mb-2">Prepare for Travel</h4>
                      <p className="text-gray-300 text-sm">
                        Check passport validity, visa requirements, and airline policies
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-full mx-auto mb-4">
                        <i className="ri-plane-takeoff-line text-white text-xl"></i>
                      </div>
                      <h4 className="text-white font-semibold mb-2">Check-in Online</h4>
                      <p className="text-gray-300 text-sm">
                        Online check-in opens 24 hours before departure
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="text-center space-y-4">
                  <div className="space-x-4">
                    <Link
                      to="/pricing"
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Book Another Flight
                    </Link>
                    <Link
                      to="/"
                      className="inline-block bg-gray-700 hover:bg-gray-600 text-white px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Back to Home
                    </Link>
                  </div>

                  <div className="text-gray-400 mt-6">
                    <p>Need assistance? Contact our 24/7 support team:</p>
                    <div className="flex items-center justify-center space-x-6 mt-2">
                      <div className="flex items-center">
                        <i className="ri-phone-line mr-2"></i>
                        <span className="text-white font-medium">+1 (555) 123-4567</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-mail-line mr-2"></i>
                        <span className="text-white font-medium">support@maveskytravels.com</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
